import { createRoot } from "react-dom/client";
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import PartySocket from "partysocket";
import { DEMO_ADVENTURE_URL, TOKEN_SIZE } from "../shared/demoScene";
import {
  ATLAS_LINK_TEST_URL,
  ATLAS_LOCAL_REWRITES,
  PORT_SMOKE_MAP_HEIGHT,
  PORT_SMOKE_MAP_WIDTH,
  PORT_SMOKE_REWRITES,
  PORT_SMOKE_URL,
} from "../shared/atlasPathRewrites";
import {
  coerceToSceneV1,
  looksLikeAtlasMap,
} from "../shared/atlasMapBridge";
import type {
  AtlasPushOptions,
  Member,
  Role,
  SceneState,
  SceneV1Payload,
  ServerMessage,
  TokenState,
  PinState,
} from "../shared/messages";
import { parseSceneV1 } from "../shared/sceneV1";
import {
  compositeFogOps,
  fogOverlayAlpha,
  type FogOp,
} from "../shared/fog";
import {
  CharacterCreatorWizard,
  type CharacterCreatorState,
} from "./CharacterCreatorWizard";
import { DAGGERHEART_ART_MANIFEST } from "../shared/daggerheartArtManifest.js";
import type { DiceRollPayload } from "../shared/diceRoll";
import { formatWeaponNoteValue, srdToPcWeapon } from "../shared/pcGear";
import { PathADiceTray } from "./PathADiceTray";
import { PcStatblockSheet } from "./PcStatblockSheet";
import "./styles.css";
import "./fsb-pc-sheet.css";

type ConnState = "idle" | "connecting" | "open" | "closed" | "error";
type Screen = "lobby" | "room";
type AssetBackend = "r2" | "room-memory";

const GM_TOKEN_STORAGE_PREFIX = "patha-gm-token:";
const CAMERA_STORAGE_PREFIX = "patha-camera:";

function readStoredGmToken(room: string): string | null {
  try {
    return sessionStorage.getItem(`${GM_TOKEN_STORAGE_PREFIX}${room}`);
  } catch {
    return null;
  }
}

function writeStoredGmToken(room: string, token: string) {
  try {
    sessionStorage.setItem(`${GM_TOKEN_STORAGE_PREFIX}${room}`, token);
  } catch {
    /* ignore quota */
  }
}

type CameraState = { x: number; y: number; zoom: number };

function readStoredCamera(room: string): CameraState | null {
  try {
    const raw = sessionStorage.getItem(`${CAMERA_STORAGE_PREFIX}${room}`);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !("zoom" in parsed) ||
      typeof (parsed as CameraState).zoom !== "number"
    ) {
      return null;
    }
    const cam = parsed as CameraState;
    const zoom = Math.min(3, Math.max(0.45, cam.zoom));
    const x = typeof cam.x === "number" && Number.isFinite(cam.x) ? cam.x : 0;
    const y = typeof cam.y === "number" && Number.isFinite(cam.y) ? cam.y : 0;
    return { x, y, zoom };
  } catch {
    return null;
  }
}

function writeStoredCamera(room: string, camera: CameraState) {
  try {
    sessionStorage.setItem(
      `${CAMERA_STORAGE_PREFIX}${room}`,
      JSON.stringify(camera),
    );
  } catch {
    /* ignore quota */
  }
}

export type HandCard = {
  id: string;
  name?: string;
  imageUrl: string;
};

const HAND_CARDS_STORAGE_PREFIX = "path_a_hand_cards_";

function readStoredHandCards(room: string, user: string): HandCard[] {
  try {
    const uKey = user.trim().toLowerCase();
    const raw =
      localStorage.getItem(`${HAND_CARDS_STORAGE_PREFIX}${room}_${uKey}`) ||
      localStorage.getItem(`${HAND_CARDS_STORAGE_PREFIX}${room}_${user}`) ||
      localStorage.getItem(`${HAND_CARDS_STORAGE_PREFIX}${room}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (c) => c && typeof c === "object" && typeof c.id === "string" && typeof c.imageUrl === "string",
    );
  } catch {
    return [];
  }
}

function writeStoredHandCards(room: string, user: string, cards: HandCard[]) {
  try {
    const uKey = user.trim().toLowerCase();
    const serialized = JSON.stringify(cards);
    localStorage.setItem(`${HAND_CARDS_STORAGE_PREFIX}${room}_${uKey}`, serialized);
    localStorage.setItem(`${HAND_CARDS_STORAGE_PREFIX}${room}`, serialized);
  } catch {
    /* ignore quota */
  }
}

function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}


function initialRoomFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return normalizeCode(params.get("room") ?? "");
}

function initialRoleFromUrl(): Role {
  const params = new URLSearchParams(window.location.search);
  const raw = (params.get("role") ?? "").trim().toLowerCase();
  return raw === "gm" ? "gm" : "player";
}

function playerOwnsToken(
  token: TokenState,
  selfId: string,
  displayName: string,
): boolean {
  if (token.ownerConnectionId && token.ownerConnectionId === selfId) return true;
  const key = (token.ownerKey ?? "").trim().toLowerCase();
  return Boolean(key && key === displayName.trim().toLowerCase());
}

function canOpenTokenSheet(
  token: TokenState,
  role: Role,
  selfId: string,
  displayName: string,
): boolean {
  return role === "gm" || playerOwnsToken(token, selfId, displayName);
}

function sheetBodyText(
  pin: SceneState["pins"][number],
  fetched: {
    pinId: string;
    phase: "loading" | "ready" | "error";
    text: string | null;
  } | null,
): string {
  if (pin.content != null && pin.content.trim()) return pin.content;
  if (fetched && fetched.pinId === pin.id) {
    if (fetched.phase === "loading") return "Loading sheet…";
    if (fetched.phase === "error") return "This sheet is not available.";
    if (fetched.text != null && fetched.text.trim()) return fetched.text;
  }
  return "(No sheet text in this room yet — GM must push notes from Obsidian.)";
}

function FogOverlay(props: {
  fog: FogOp[];
  mapWidth: number;
  mapHeight: number;
  role: Role;
  tokens?: TokenState[];
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { fog, mapWidth, mapHeight, role, tokens } = props;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (fog.length === 0 || mapWidth < 1 || mapHeight < 1) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 1;
      canvas.height = 1;
      return;
    }
    const tokenClears = (tokens ?? [])
      .filter((t) => role === "gm" || !t.gmOnly)
      .map((t) => ({
        x: t.x,
        y: t.y,
        size: t.size,
      }));
    compositeFogOps(ctx, fog, mapWidth, mapHeight, 0.5, tokenClears);
  }, [fog, mapWidth, mapHeight, tokens, role]);

  if (fog.length === 0) return null;

  return (
    <canvas
      ref={canvasRef}
      className="board-fog"
      data-testid="board-fog"
      data-fog-count={fog.length}
      data-fog-role={role}
      aria-hidden
      style={{ opacity: fogOverlayAlpha(role) }}
    />
  );
}

function normalizePinIcon(raw: string | null | undefined): string {
  const key = (raw ?? "pin").trim().toLowerCase();
  if (!key) return "pin";
  if (key === "book") return "lore";
  if (key === "chest") return "treasure";
  return key;
}

function PinIconGlyph(props: { icon: string | null | undefined }) {
  const icon = normalizePinIcon(props.icon);
  // Teal/petrol Atlas-like markers (not red pushpins).
  return (
    <span className="map-pin-icon" data-pin-icon={icon} aria-hidden>
      <svg viewBox="0 0 24 24" width="22" height="28" focusable="false">
        {icon === "lore" ? (
          <>
            <path
              className="map-pin-stem"
              d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z"
            />
            <path
              className="map-pin-note"
              d="M9 7.2h6.2v7.2c0 .4-.4.7-.8.7H9.8c-.4 0-.8-.3-.8-.7V7.2z"
            />
            <path className="map-pin-fold" d="M10.2 9.2h3.8M10.2 11.2h3.8M10.2 13.2h2.4" />
          </>
        ) : icon === "treasure" ? (
          <>
            <path
              className="map-pin-stem"
              d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z"
            />
            <path
              className="map-pin-note"
              d="M8.4 9.2h7.2v5.4H8.4zM9.2 9.2V8.4c0-.7.6-1.2 1.3-1.2h3c.7 0 1.3.5 1.3 1.2v.8"
            />
            <path className="map-pin-fold" d="M12 9.2v5.4M8.4 11.2h7.2" />
          </>
        ) : icon === "location" ? (
          <>
            <path
              className="map-pin-stem"
              d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z"
            />
            <circle className="map-pin-note" cx="12" cy="10" r="2.4" />
          </>
        ) : icon === "swamp" ? (
          <>
            <path
              className="map-pin-stem"
              d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z"
            />
            <path
              className="map-pin-note"
              d="M8.5 12.5c1.2-2.2 2.6-3.4 3.5-3.8 0.9 0.4 2.3 1.6 3.5 3.8-1.1 0.4-2.3 0.6-3.5 0.6s-2.4-0.2-3.5-0.6z"
            />
            <path className="map-pin-fold" d="M12 8.6v4.5" />
          </>
        ) : icon === "dungeon" ? (
          <>
            <path
              className="map-pin-stem"
              d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z"
            />
            <path
              className="map-pin-note"
              d="M9 14.2V9.2l3-1.6 3 1.6v5H9z"
            />
            <path className="map-pin-fold" d="M12 12.2v2M10.4 14.2v-1.4h3.2v1.4" />
          </>
        ) : (
          <>
            <path
              className="map-pin-stem"
              d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z"
            />
            <path
              className="map-pin-note"
              d="M9.2 6.4h5.1c.5 0 .9.4.9.9v4.2c0 .5-.4.9-.9.9h-3.2l-1.9 1.5v-1.5H9.2c-.5 0-.9-.4-.9-.9V7.3c0-.5.4-.9.9-.9z"
            />
            <path
              className="map-pin-fold"
              d="M13.4 6.4v1.7c0 .3.2.5.5.5H15.5"
            />
          </>
        )}
      </svg>
    </span>
  );
}

function canMoveToken(
  token: TokenState,
  selfId: string | null,
  role: Role,
): boolean {
  if (!selfId) return false;
  if (role === "gm") return true;
  return token.ownerConnectionId === selfId;
}

function ownerLabel(
  token: TokenState,
  members: Member[],
): string {
  if (!token.ownerConnectionId) {
    return token.ownerKey ? `seat: ${token.ownerKey}` : "unassigned";
  }
  const m = members.find((x) => x.connectionId === token.ownerConnectionId);
  return m ? m.displayName : "unknown";
}

function App() {
  const [screen, setScreen] = useState<Screen>("lobby");
  const [roomCode, setRoomCode] = useState(initialRoomFromUrl);
  const [joinCodeInput] = useState(initialRoomFromUrl);
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>(initialRoleFromUrl);
  const [connState, setConnState] = useState<ConnState>("idle");
  const [members, setMembers] = useState<Member[]>([]);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [scene, setScene] = useState<SceneState | null>(null);
  const [openPinId, setOpenPinId] = useState<string | null>(null);
  const [sheetFetch, setSheetFetch] = useState<{
    pinId: string;
    phase: "loading" | "ready" | "error";
    text: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejectNotice, setRejectNotice] = useState<string | null>(null);
  const [lastMoveMs, setLastMoveMs] = useState<number | null>(null);
  const [scenePaste, setScenePaste] = useState("");
  const [loadNote, setLoadNote] = useState<string | null>(null);
  const [assetBackend, setAssetBackend] = useState<AssetBackend | null>(null);
  const [copyNote, setCopyNote] = useState<string | null>(null);
  const [camera, setCamera] = useState<CameraState>(
    () => readStoredCamera(initialRoomFromUrl()) ?? { x: 0, y: 0, zoom: 1 },
  );
  const [gmToken, setGmToken] = useState<string | null>(null);
  const [gmSessionClaimed, setGmSessionClaimed] = useState(false);
  const [handOpen, setHandOpen] = useState(false);
  const [handCards, setHandCards] = useState<HandCard[]>([]);
  const [previewCard, setPreviewCard] = useState<HandCard | null>(null);
  const [newCardUrl, setNewCardUrl] = useState("");
  const [newCardName, setNewCardName] = useState("");
  const [characterCreatorOpen, setCharacterCreatorOpen] = useState(false);
  const [hoveredToken, setHoveredToken] = useState<TokenState | null>(null);
  const hoveredTokenRef = useRef<TokenState | null>(null);
  const [hoveredPin, setHoveredPin] = useState<PinState | null>(null);
  const hoveredPinRef = useRef<PinState | null>(null);
  const [openTokenSheet, setOpenTokenSheet] = useState<TokenState | null>(null);
  const [tokenSheetNote, setTokenSheetNote] = useState<{
    tokenId: string;
    text: string;
  } | null>(null);
  const [rollResult, setRollResult] = useState<DiceRollPayload | null>(null);
  const lastShownRollIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!rollResult) return;
    const timer = window.setTimeout(() => setRollResult(null), 12000);
    return () => window.clearTimeout(timer);
  }, [rollResult]);
  const [tokenContextMenu, setTokenContextMenu] = useState<{
    token: TokenState;
    x: number;
    y: number;
  } | null>(null);
  const cardFileInputRef = useRef<HTMLInputElement | null>(null);
  const gmTokenRef = useRef<string | null>(null);
  const socketRef = useRef<PartySocket | null>(null);
  const sessionRef = useRef(0);
  const moveSentAtRef = useRef<Map<string, number>>(new Map());
  const dragRef = useRef<{
    tokenId: string;
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const panRef = useRef<{
    pointerId: number;
    originX: number;
    originY: number;
    startCamX: number;
    startCamY: number;
  } | null>(null);
  const cameraRef = useRef(camera);
  const sceneRef = useRef(scene);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    cameraRef.current = camera;
  }, [camera]);

  useEffect(() => {
    // Only persist while seated — lobby mount must not clobber stored zoom (QA-04).
    if (!roomCode || screen !== "room") return;
    writeStoredCamera(roomCode, camera);
  }, [roomCode, camera, screen]);

  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  useEffect(() => {
    gmTokenRef.current = gmToken;
  }, [gmToken]);

  // Load player's local card hand when entering room
  useEffect(() => {
    if (screen === "room" && roomCode) {
      const user = displayName.trim() || selfId || "player";
      const stored = readStoredHandCards(roomCode, user);
      setHandCards(stored);
    }
  }, [screen, roomCode, displayName, selfId]);

  useEffect(() => {
    hoveredTokenRef.current = hoveredToken;
  }, [hoveredToken]);

  useEffect(() => {
    hoveredPinRef.current = hoveredPin;
  }, [hoveredPin]);

  // Command-hover keydown listener: opens stats and notes for hovered token or pin
  useEffect(() => {
    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Meta" || ev.key === "Control" || ev.metaKey || ev.ctrlKey) {
        if (hoveredTokenRef.current) {
          if (canOpenTokenSheet(hoveredTokenRef.current, role, selfId, displayName)) {
            setOpenTokenSheet(hoveredTokenRef.current);
          }
        } else if (hoveredPinRef.current) {
          const isCreatePin = /create\s*character/i.test(hoveredPinRef.current.label.trim());
          if (isCreatePin) {
            setCharacterCreatorOpen(true);
          } else {
            setOpenPinId(hoveredPinRef.current.id);
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [role, selfId, displayName]);

  const updateHandCards = (updater: (prev: HandCard[]) => HandCard[]) => {
    setHandCards((prev) => {
      const next = updater(prev);
      const user = displayName.trim() || selfId || "player";
      if (roomCode) {
        writeStoredHandCards(roomCode, user, next);
      }
      return next;
    });
  };

  const addCardByUrl = (url: string, name?: string) => {
    const trimmed = url.trim();
    if (!trimmed) return;
    const newCard: HandCard = {
      id: `card_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      imageUrl: trimmed,
      name: (name || "").trim() || undefined,
    };
    updateHandCards((prev) => [...prev, newCard]);
    setNewCardUrl("");
    setNewCardName("");
  };

  const handleCardFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        addCardByUrl(reader.result, file.name.replace(/\.[^.]+$/, ""));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removeCard = (id: string) => {
    updateHandCards((prev) => prev.filter((c) => c.id !== id));
    if (previewCard?.id === id) {
      setPreviewCard(null);
    }
  };

  const handleCharacterConfirmed = (char: CharacterCreatorState) => {
    const charName = char.name.trim() || char.selectedClass?.name || "Hero";
    const hpMax = char.selectedClass?.startingHp ?? 6;
    const evasion = char.selectedClass?.startingEvasion ?? 10;
    const armorScore = char.armor?.baseScore ?? 3;
    const armorMajor = char.armor?.baseMajor ?? 6;
    const armorSevere = char.armor?.baseSevere ?? 12;
    const className = char.selectedClass?.name ?? "Warrior";
    const subclassName = char.selectedSubclass?.name ?? "Specialist";
    const ancestryName = char.isMixedAncestry
      ? `Mixed (${char.mixedAncestryTop?.name}/${char.mixedAncestryBottom?.name})`
      : char.ancestry?.name ?? "Human";
    const communityName = char.community?.name ?? "Wanderborne";

    // Unique token ID for this new character
    const tokenId = `tok_${charName.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_${Date.now().toString(36)}`;
    const notePath = `atlas-vtt/statblocks/PC-${charName}-${tokenId}.md`;

    // Portrait URL from art manifest (class card)
    const classAssetId = `class-${className.toLowerCase()}`;
    const classAsset = DAGGERHEART_ART_MANIFEST[classAssetId];
    const portraitUrl = classAsset
      ? `/${classAsset.path.replace(/^public\//, "")}`
      : "/demo-adventure/token-rowan.svg";

    // Build the finished stat block note
    const noteContent = `---
tokenId: ${tokenId}
statblockId: ${tokenId}
statblock: true
layout: Daggerheart PC
name: "${charName}"
class: "${className}"
subclass: "${subclassName}"
ancestry: "${ancestryName}"
community: "${communityName}"
level: 1
proficiency: 1
evasion: ${evasion}
max_hp: ${hpMax}
hp: ${hpMax}
marked_hp: 0
max_stress: 6
stress: 6
marked_fear: 0
max_hope: 2
hope: 2
armor: ${armorScore}
marked_armor: 0
thresholds: "${armorMajor}/${armorSevere}"
agility: ${char.traits.Agility ?? 0}
strength: ${char.traits.Strength ?? 0}
finesse: ${char.traits.Finesse ?? 0}
instinct: ${char.traits.Instinct ?? 0}
presence: ${char.traits.Presence ?? 0}
knowledge: ${char.traits.Knowledge ?? 0}
experience: "${char.experience1}, ${char.experience2}"
primary_weapon: "${char.primaryWeapon ? formatWeaponNoteValue(srdToPcWeapon(char.primaryWeapon, "primary")) : "Unarmed"}"
${char.secondaryWeapon ? `secondary_weapon: "${formatWeaponNoteValue(srdToPcWeapon(char.secondaryWeapon, "secondary"))}"\n` : ""}\
armor_name: "${char.armor?.name ?? "Leather Armor"}"
potion: "${char.potionChoice}"
domain_cards: "${char.domainCards.map((c) => c.name).join(", ")}"
background: "${char.backgroundText.replace(/"/g, "'")}"
connections: "${char.connectionsText.replace(/"/g, "'")}"
---

\`\`\`statblock
layout: Daggerheart PC
name: ${charName}
class: ${className}
subclass: ${subclassName}
ancestry: ${ancestryName}
community: ${communityName}
level: 1
proficiency: 1
evasion: ${evasion}
hp: ${hpMax}
armor: ${armorScore}
thresholds: ${armorMajor}/${armorSevere}
agility: ${char.traits.Agility ?? 0}
strength: ${char.traits.Strength ?? 0}
finesse: ${char.traits.Finesse ?? 0}
instinct: ${char.traits.Instinct ?? 0}
presence: ${char.traits.Presence ?? 0}
knowledge: ${char.traits.Knowledge ?? 0}
primary_weapon: ${char.primaryWeapon ? formatWeaponNoteValue(srdToPcWeapon(char.primaryWeapon, "primary")) : "Unarmed"}
${char.secondaryWeapon ? `secondary_weapon: ${formatWeaponNoteValue(srdToPcWeapon(char.secondaryWeapon, "secondary"))}\n` : ""}\
experience: ${char.experience1}, ${char.experience2}
\`\`\`
`;

    // Randomize initial board placement around center
    const mapW = scene?.map.width ?? 960;
    const mapH = scene?.map.height ?? 560;
    const posX = Math.round(mapW * 0.45 + (Math.random() * 0.1 * mapW));
    const posY = Math.round(mapH * 0.45 + (Math.random() * 0.1 * mapH));

    const newToken: TokenState = {
      id: tokenId,
      name: charName,
      artUrl: portraitUrl,
      x: posX,
      y: posY,
      ownerKey: displayName.trim() || selfId,
      ownerConnectionId: selfId,
      gmOnly: false,
      size: 70,
      showRing: true,
      ringColor: "#c4a574",
      showNameplate: true,
      hp: { current: hpMax, max: hpMax },
      stress: { current: 0, max: 6 },
      boardCreated: true,
      notePath,
    };

    // 1. Send token.create to party server
    sendJson({
      type: "token.create",
      token: newToken,
      notePath,
      noteContent,
    });

    // 2. Put 5 cards into that player's private hand:
    // (ancestry, community, subclass foundation, and the 2 chosen level 1 domain cards)
    const fiveCards: HandCard[] = [];

    // 1) Ancestry card
    if (char.isMixedAncestry) {
      const topSlug = char.mixedAncestryTop?.name.toLowerCase();
      const ancAsset = DAGGERHEART_ART_MANIFEST["ancestry-mixed"] || (topSlug ? DAGGERHEART_ART_MANIFEST[`ancestry-${topSlug}`] : null);
      fiveCards.push({
        id: `card_ancestry_mixed_${Date.now()}_1`,
        name: `Mixed Ancestry (${char.mixedAncestryTop?.name ?? "Ancestry"}/${char.mixedAncestryBottom?.name ?? "Ancestry"})`,
        imageUrl: ancAsset
          ? `/${ancAsset.path.replace(/^public\//, "")}`
          : "/assets/cards/ancestry-mixed.svg",
      });
    } else {
      const ancSlug = char.ancestry?.name.toLowerCase() ?? "human";
      const ancAsset = DAGGERHEART_ART_MANIFEST[`ancestry-${ancSlug}`];
      fiveCards.push({
        id: `card_ancestry_${ancSlug}_${Date.now()}_1`,
        name: char.ancestry?.name ?? "Ancestry",
        imageUrl: ancAsset
          ? `/${ancAsset.path.replace(/^public\//, "")}`
          : "/assets/cards/ancestry-human.svg",
      });
    }

    // 2) Community card
    const comSlug = char.community?.name.toLowerCase() ?? "wanderborne";
    const comAsset = DAGGERHEART_ART_MANIFEST[`community-${comSlug}`];
    fiveCards.push({
      id: `card_community_${comSlug}_${Date.now()}_2`,
      name: char.community?.name ?? "Community",
      imageUrl: comAsset
        ? `/${comAsset.path.replace(/^public\//, "")}`
        : "/assets/cards/community-wanderborne.svg",
    });

    // 3) Subclass foundation card
    const clsSlug = char.selectedClass?.name.toLowerCase() ?? "warrior";
    const scSlug = (char.selectedSubclass?.name ?? "specialist").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const scId = `subclass-${clsSlug}-${scSlug}`;
    const scAsset = DAGGERHEART_ART_MANIFEST[scId];
    fiveCards.push({
      id: `card_subclass_${scId}_${Date.now()}_3`,
      name: `${char.selectedSubclass?.name ?? "Subclass"} (Foundation)`,
      imageUrl: scAsset
        ? `/${scAsset.path.replace(/^public\//, "")}`
        : `/assets/cards/class-${clsSlug}.svg`,
    });

    // 4 & 5) 2 Level 1 Domain Cards
    char.domainCards.forEach((dCard, i) => {
      const dSlug = dCard.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const cardId = `card-${dCard.domain.toLowerCase()}-${dSlug}`;
      const asset = DAGGERHEART_ART_MANIFEST[cardId];
      fiveCards.push({
        id: `card_domain_${cardId}_${Date.now()}_${4 + i}`,
        name: `${dCard.name} [${dCard.domain}]`,
        imageUrl: asset
          ? `/${asset.path.replace(/^public\//, "")}`
          : "/assets/cards/weapon-tier-1-generic.svg",
      });
    });

    // Put into player private hand only (localStorage)
    updateHandCards((prev) => [...prev, ...fiveCards]);
    setHandOpen(true);
    setCharacterCreatorOpen(false);

    setLoadNote(`Character ${charName} created! Token placed and 5 cards added to your hand.`);
  };

  const openPin = scene?.pins.find((p) => p.id === openPinId) ?? null;
  const openSheetKey = openPin
    ? `${openPin.id}\n${openPin.notePath ?? ""}\n${openPin.content ?? ""}`
    : "";

  useEffect(() => {
    if (!openPinId || !openPin) {
      setSheetFetch(null);
      return;
    }
    if (openPin.content != null && openPin.content.trim()) {
      setSheetFetch(null);
      return;
    }
    if (!openPin.notePath) {
      setSheetFetch({ pinId: openPin.id, phase: "ready", text: null });
      return;
    }
    const ac = new AbortController();
    const pinId = openPin.id;
    const notePath = openPin.notePath;
    setSheetFetch({ pinId, phase: "loading", text: null });
    const headers: Record<string, string> = {};
    if (role === "gm" && gmToken) {
      headers.Authorization = `Bearer ${gmToken}`;
    }
    void fetch(
      `/parties/main/${encodeURIComponent(roomCode)}/notes?path=${encodeURIComponent(notePath)}&viewer=${encodeURIComponent(displayName)}`,
      { headers, signal: ac.signal },
    )
      .then(async (res) => {
        if (!res.ok) {
          setSheetFetch({ pinId, phase: "error", text: null });
          return;
        }
        const body = (await res.json()) as { content?: unknown };
        setSheetFetch({
          pinId,
          phase: "ready",
          text: typeof body.content === "string" ? body.content : null,
        });
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setSheetFetch({ pinId, phase: "error", text: null });
      });
    return () => ac.abort();
  }, [openSheetKey, openPin, role, roomCode, gmToken, displayName]);

  useEffect(() => {
    if (!openTokenSheet) {
      setTokenSheetNote(null);
      return;
    }
    const matching = scene?.pins.find(
      (p) =>
        p.id === openTokenSheet.id ||
        p.id === `pin-${openTokenSheet.id}` ||
        p.label.toLowerCase() === openTokenSheet.name.toLowerCase(),
    );
    const inline = matching?.content?.trim() ?? "";
    const fromToken = openTokenSheet.noteContent?.trim() ?? "";
    if (fromToken) {
      setTokenSheetNote({ tokenId: openTokenSheet.id, text: fromToken });
      return;
    }
    if (inline) {
      setTokenSheetNote({ tokenId: openTokenSheet.id, text: inline });
      return;
    }
    const candidates = [
      openTokenSheet.notePath?.trim() || "",
      matching?.notePath?.trim() || "",
      `atlas-vtt/statblocks/PC-${openTokenSheet.name}-${openTokenSheet.id}.md`,
      `atlas-vtt/statblocks/PC-${openTokenSheet.name}.md`,
      `atlas-vtt/statblocks/${openTokenSheet.name}.md`,
    ].filter((p, i, arr) => p.length > 0 && arr.indexOf(p) === i);
    const ac = new AbortController();
    const tokenId = openTokenSheet.id;
    const headers: Record<string, string> = {};
    if (role === "gm" && gmToken) {
      headers.Authorization = `Bearer ${gmToken}`;
    }
    void (async () => {
      for (const notePath of candidates) {
        try {
          const qs = new URLSearchParams({
            path: notePath,
            viewer: displayName,
            tokenId,
            tokenName: openTokenSheet.name,
          });
          const res = await fetch(
            `/parties/main/${encodeURIComponent(roomCode)}/notes?${qs.toString()}`,
            { headers, signal: ac.signal },
          );
          if (!res.ok) continue;
          const body = (await res.json()) as { content?: unknown };
          if (typeof body.content === "string" && body.content.trim()) {
            setTokenSheetNote({ tokenId, text: body.content });
            return;
          }
        } catch (err: unknown) {
          if (err instanceof DOMException && err.name === "AbortError") return;
        }
      }
      setTokenSheetNote({ tokenId, text: "" });
    })();
    return () => ac.abort();
  }, [openTokenSheet, scene?.pins, role, roomCode, gmToken, displayName]);

  useEffect(() => {
    return () => {
      sessionRef.current += 1;
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    function pointToBoard(clientX: number, clientY: number) {
      const current = sceneRef.current;
      const board = boardRef.current;
      if (!current || !board) return null;
      const rect = board.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      return {
        x: ((clientX - rect.left) / rect.width) * current.map.width,
        y: ((clientY - rect.top) / rect.height) * current.map.height,
      };
    }
    function onMove(e: PointerEvent) {
      const drag = dragRef.current;
      if (drag && drag.pointerId === e.pointerId) {
        const point = pointToBoard(e.clientX, e.clientY);
        if (!point) return;
        const x = point.x - drag.offsetX;
        const y = point.y - drag.offsetY;
        setScene((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            tokens: prev.tokens.map((t) =>
              t.id === drag.tokenId ? { ...t, x, y } : t,
            ),
          };
        });
        const socket = socketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
          moveSentAtRef.current.set(drag.tokenId, performance.now());
          socket.send(
            JSON.stringify({
              type: "token.move",
              tokenId: drag.tokenId,
              x,
              y,
            }),
          );
        }
        return;
      }
      const pan = panRef.current;
      if (!pan || pan.pointerId !== e.pointerId) return;
      setCamera((prev) => ({
        ...prev,
        x: pan.startCamX + (e.clientX - pan.originX),
        y: pan.startCamY + (e.clientY - pan.originY),
      }));
    }
    function onUp(e: PointerEvent) {
      if (dragRef.current?.pointerId === e.pointerId) {
        dragRef.current = null;
      }
      if (panRef.current?.pointerId === e.pointerId) {
        panRef.current = null;
      }
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  function clearRoomQuery() {
    const url = new URL(window.location.href);
    url.searchParams.delete("room");
    window.history.replaceState({}, "", url.pathname);
  }

  function roomShareUrl(code: string): string {
    const url = new URL(window.location.href);
    url.search = "";
    url.hash = "";
    url.searchParams.set("room", normalizeCode(code));
    return url.toString();
  }

  async function copyText(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopyNote(`Copied ${label}`);
      window.setTimeout(() => setCopyNote(null), 2500);
    } catch {
      setCopyNote(`Could not copy — select the ${label} manually`);
      window.setTimeout(() => setCopyNote(null), 4000);
    }
  }

  function disconnect() {
    sessionRef.current += 1;
    socketRef.current?.close();
    socketRef.current = null;
    setConnState("idle");
    setMembers([]);
    setSelfId(null);
    setScene(null);
    setScreen("lobby");
    setError(null);
    setRejectNotice(null);
    setLastMoveMs(null);
    setLoadNote(null);
    setAssetBackend(null);
    setCopyNote(null);
    setGmToken(null);
    setGmSessionClaimed(false);
    setCamera({ x: 0, y: 0, zoom: 1 });
    clearRoomQuery();
  }

  async function claimGmSession(
    room: string,
    existingToken?: string | null,
  ): Promise<string> {
    const headers: Record<string, string> = {};
    if (existingToken) {
      headers.Authorization = `Bearer ${existingToken}`;
    }
    const res = await fetch(
      `/parties/main/${encodeURIComponent(room)}/gm/session`,
      { method: "POST", headers },
    );
    const body: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      const errMsg =
        body &&
        typeof body === "object" &&
        "error" in body &&
        typeof (body as { error: unknown }).error === "string"
          ? (body as { error: string }).error
          : `GM session claim failed (HTTP ${res.status})`;
      throw new Error(errMsg);
    }
    if (
      !body ||
      typeof body !== "object" ||
      typeof (body as { gmToken?: unknown }).gmToken !== "string"
    ) {
      throw new Error("GM session response missing gmToken");
    }
    const token = (body as { gmToken: string }).gmToken;
    writeStoredGmToken(room, token);
    setGmToken(token);
    setGmSessionClaimed(true);
    return token;
  }

  async function enterRoom(
    code: string,
    name: string,
    chosenRole: Role,
    opts: { claimGm?: boolean } = {},
  ) {
    const normalized = normalizeCode(code);
    const trimmedName = name.trim();
    if (normalized.length < 4) {
      setError("Room code must be at least 4 characters");
      return;
    }
    if (!trimmedName) {
      setError("Display name is required");
      return;
    }

    sessionRef.current += 1;
    const session = sessionRef.current;
    socketRef.current?.close();

    setError(null);
    setRejectNotice(null);
    setMembers([]);
    setSelfId(null);
    setScene(null);
    setLastMoveMs(null);
    setLoadNote(null);
    setAssetBackend(null);
    setCopyNote(null);
    setCamera(readStoredCamera(normalized) ?? { x: 0, y: 0, zoom: 1 });
    setRoomCode(normalized);
    setRole(chosenRole);
    setConnState("connecting");
    setScreen("room");

    let sessionToken: string | null = null;
    if (chosenRole === "gm" && opts.claimGm !== false) {
      try {
        const stored = readStoredGmToken(normalized);
        sessionToken = await claimGmSession(normalized, stored);
      } catch (err) {
        if (session !== sessionRef.current) return;
        setConnState("error");
        setError(err instanceof Error ? err.message : "GM session claim failed");
        setScreen("lobby");
        return;
      }
    } else {
      const stored = readStoredGmToken(normalized);
      sessionToken = stored;
      setGmToken(stored);
      setGmSessionClaimed(false);
    }

    if (session !== sessionRef.current) return;

    const url = new URL(window.location.href);
    url.searchParams.set("room", normalized);
    window.history.replaceState({}, "", `${url.pathname}?${url.searchParams}`);

    const socket = new PartySocket({
      host: window.location.host,
      room: normalized,
      party: "main",
    });
    socketRef.current = socket;

    const onOpen = () => {
      if (session !== sessionRef.current) return;
      setConnState("open");
      const joinPayload: {
        type: "join";
        displayName: string;
        role: Role;
        gmToken?: string;
      } = {
        type: "join",
        displayName: trimmedName,
        role: chosenRole,
      };
      if (chosenRole === "gm" && sessionToken) {
        joinPayload.gmToken = sessionToken;
      }
      socket.send(JSON.stringify(joinPayload));
    };
    const onClose = () => {
      if (session !== sessionRef.current) return;
      setConnState("closed");
    };
    const onError = () => {
      if (session !== sessionRef.current) return;
      setConnState("error");
      setError("Socket error — is the PartyKit server running?");
    };
    const onMessage = (event: MessageEvent) => {
      if (session !== sessionRef.current) return;
      if (typeof event.data !== "string") return;
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      switch (msg.type) {
        case "hello":
          setSelfId(msg.connectionId);
          setAssetBackend(msg.assetBackend);
          setGmSessionClaimed(msg.gmSessionClaimed);
          break;
        case "presence":
          setMembers(msg.members);
          break;
        case "scene":
          setScene(
            msg.scene
              ? {
                  ...msg.scene,
                  pins: msg.scene.pins ?? [],
                  fog: msg.scene.fog ?? [],
                  widgets: msg.scene.widgets ?? [],
                }
              : null,
          );
          setOpenPinId(null);
          // Keep camera/zoom across GM scene pushes — only reset on enter/leave.
          break;
        case "token.moved": {
          const sentAt = moveSentAtRef.current.get(msg.tokenId);
          if (sentAt !== undefined) {
            setLastMoveMs(Math.round(performance.now() - sentAt));
            moveSentAtRef.current.delete(msg.tokenId);
          }
          setScene((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              tokens: prev.tokens.map((t) =>
                t.id === msg.tokenId ? { ...t, x: msg.x, y: msg.y } : t,
              ),
            };
          });
          break;
        }
        case "token.updated":
          setScene((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              tokens: prev.tokens.map((t) =>
                t.id === msg.tokenId
                  ? { ...t, artUrl: msg.artUrl || t.artUrl }
                  : t,
              ),
            };
          });
          setOpenTokenSheet((prev) =>
            prev && prev.id === msg.tokenId
              ? { ...prev, artUrl: msg.artUrl || prev.artUrl }
              : prev,
          );
          break;
        case "token.deleted":
          setScene((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              tokens: prev.tokens.filter((t) => t.id !== msg.tokenId),
              initiative: (prev.initiative ?? []).filter((id) => id !== msg.tokenId),
            };
          });
          setOpenTokenSheet((prev) => (prev?.id === msg.tokenId ? null : prev));
          break;
        case "token.move.rejected":
          setRejectNotice(msg.reason);
          setError(msg.reason);
          break;
        case "error":
          setError(msg.message);
          break;
        case "dice.rolled":
          if (msg.roll.pending) break;
          if (lastShownRollIdRef.current === msg.roll.id) break;
          lastShownRollIdRef.current = msg.roll.id;
          setRollResult(msg.roll);
          break;
        default: {
          const _exhaustive: never = msg;
          void _exhaustive;
        }
      }
    };

    socket.addEventListener("open", onOpen);
    socket.addEventListener("close", onClose);
    socket.addEventListener("error", onError);
    socket.addEventListener("message", onMessage);
  }

  function sendJson(payload: unknown) {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify(payload));
  }

  function publishBoardRoll(roll: DiceRollPayload) {
    lastShownRollIdRef.current = roll.id;
    sendJson({ type: "dice.roll", roll });
    if (roomCode) {
      void fetch(`/parties/main/${encodeURIComponent(roomCode)}/dice/roll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roll }),
      }).catch(() => {
        // WebSocket path already attempted.
      });
    }
  }

  function defaultAtlasOptions(
    overrides: AtlasPushOptions = {},
  ): AtlasPushOptions {
    return {
      pathRewrites: { ...ATLAS_LOCAL_REWRITES, ...overrides.pathRewrites },
      mapWidth: overrides.mapWidth ?? 960,
      mapHeight: overrides.mapHeight ?? 560,
      id: overrides.id,
      name: overrides.name,
      assetBaseUrl: overrides.assetBaseUrl,
    };
  }

  function pushAtlasDocument(
    raw: unknown,
    sourceLabel: string,
    optionOverrides: AtlasPushOptions = {},
  ) {
    if (!looksLikeAtlasMap(raw) || typeof raw !== "object" || raw === null) {
      setError("Not a valid Atlas .atlasmap document");
      setLoadNote(null);
      return;
    }
    const options = defaultAtlasOptions(optionOverrides);
    const bridged = coerceToSceneV1(raw, options);
    if (!bridged.ok) {
      setError(bridged.error);
      setLoadNote(null);
      return;
    }
    sendJson({
      type: "scene.push.atlas",
      atlasmap: raw as Record<string, unknown>,
      options,
      gmToken: gmTokenRef.current ?? undefined,
    });
    setError(null);
    setLoadNote(`Pushed Atlas scene ${sourceLabel}: ${bridged.scene.name}`);
  }

  function loadSceneDocument(raw: unknown, sourceLabel: string) {
    if (looksLikeAtlasMap(raw)) {
      pushAtlasDocument(raw, sourceLabel);
      return;
    }
    const parsed = parseSceneV1(raw);
    if (!parsed.ok) {
      setError(parsed.error);
      setLoadNote(null);
      return;
    }
    const payload = raw as SceneV1Payload;
    sendJson({
      type: "scene.load",
      scene: payload,
      gmToken: gmTokenRef.current ?? undefined,
    });
    setError(null);
    setLoadNote(`Loaded ${sourceLabel}: ${parsed.scene.name}`);
  }

  function onPasteLoad() {
    let raw: unknown;
    try {
      raw = JSON.parse(scenePaste);
    } catch {
      setError("Paste is not valid JSON");
      return;
    }
    loadSceneDocument(raw, "paste");
  }

  async function onFilePick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const raw: unknown = JSON.parse(text);
      loadSceneDocument(raw, file.name);
    } catch {
      setError("Could not parse scene file as JSON");
    }
  }

  async function onPushAtlasLinkTest() {
    try {
      const res = await fetch(ATLAS_LINK_TEST_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw: unknown = await res.json();
      pushAtlasDocument(raw, "Atlas Link Test");
    } catch {
      setError("Failed to fetch Atlas Link Test .atlasmap");
    }
  }

  async function onPushPortSmokeTest() {
    try {
      const res = await fetch(PORT_SMOKE_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw: unknown = await res.json();
      pushAtlasDocument(raw, "PortSmokeTest", {
        pathRewrites: { ...PORT_SMOKE_REWRITES },
        mapWidth: PORT_SMOKE_MAP_WIDTH,
        mapHeight: PORT_SMOKE_MAP_HEIGHT,
      });
    } catch {
      setError("Failed to fetch PortSmokeTest .atlasmap");
    }
  }

  async function onLoadDemoAdventure() {
    try {
      const res = await fetch(DEMO_ADVENTURE_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw: unknown = await res.json();
      loadSceneDocument(raw, "demo adventure");
    } catch {
      setError("Failed to fetch demo adventure scene");
    }
  }

  function assignToken(tokenId: string, ownerConnectionId: string | null) {
    sendJson({ type: "token.assign", tokenId, ownerConnectionId });
  }

  function matchSeatsByOwnerKey() {
    if (!scene) return;
    const players = members.filter((m) => m.role === "player");
    let matched = 0;
    for (const token of scene.tokens) {
      if (!token.ownerKey) continue;
      const key = token.ownerKey.trim().toLowerCase();
      const player = players.find(
        (p) => p.displayName.trim().toLowerCase() === key,
      );
      if (!player) continue;
      if (token.ownerConnectionId === player.connectionId) continue;
      assignToken(token.id, player.connectionId);
      matched += 1;
    }
    setLoadNote(
      matched > 0
        ? `Matched ${matched} token seat(s) by ownerKey ≈ display name`
        : "No ownerKey matches to player display names",
    );
  }

  function clientToBoard(clientX: number, clientY: number) {
    if (!boardRef.current || !scene) return null;
    const rect = boardRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const scaleX = scene.map.width / rect.width;
    const scaleY = scene.map.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  function onTokenPointerDown(token: TokenState, e: ReactPointerEvent) {
    if (!canMoveToken(token, selfId, role)) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const point = clientToBoard(e.clientX, e.clientY);
    if (!point) return;
    dragRef.current = {
      tokenId: token.id,
      pointerId: e.pointerId,
      offsetX: point.x - token.x,
      offsetY: point.y - token.y,
    };
    panRef.current = null;
    setRejectNotice(null);
  }

  function onViewportPointerDown(e: ReactPointerEvent) {
    if (e.button !== 0 && e.button !== 1) return;
    if (dragRef.current) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest?.(".token[data-movable='true']")) return;
    if (target?.closest?.(".map-pin")) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const cam = cameraRef.current;
    panRef.current = {
      pointerId: e.pointerId,
      originX: e.clientX,
      originY: e.clientY,
      startCamX: cam.x,
      startCamY: cam.y,
    };
  }

  function onViewportPointerUp(e: ReactPointerEvent) {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
    if (panRef.current?.pointerId === e.pointerId) {
      panRef.current = null;
    }
  }

  function onViewportWheel(e: ReactWheelEvent<HTMLDivElement>) {
    if (!viewportRef.current) return;
    e.preventDefault();
    const rect = viewportRef.current.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    setCamera((prev) => {
      const nextZoom = Math.min(3, Math.max(0.45, prev.zoom * factor));
      const ratio = nextZoom / prev.zoom;
      return {
        zoom: nextZoom,
        x: localX - (localX - prev.x) * ratio,
        y: localY - (localY - prev.y) * ratio,
      };
    });
  }

  function nudgeZoom(direction: 1 | -1) {
    if (!viewportRef.current) {
      setCamera((prev) => ({
        ...prev,
        zoom: Math.min(3, Math.max(0.45, prev.zoom * (direction > 0 ? 1.2 : 1 / 1.2))),
      }));
      return;
    }
    const rect = viewportRef.current.getBoundingClientRect();
    const localX = rect.width / 2;
    const localY = rect.height / 2;
    const factor = direction > 0 ? 1.2 : 1 / 1.2;
    setCamera((prev) => {
      const nextZoom = Math.min(3, Math.max(0.45, prev.zoom * factor));
      const ratio = nextZoom / prev.zoom;
      return {
        zoom: nextZoom,
        x: localX - (localX - prev.x) * ratio,
        y: localY - (localY - prev.y) * ratio,
      };
    });
  }

  function resetCamera() {
    setCamera({ x: 0, y: 0, zoom: 1 });
  }

  function onLobbySubmit(e: FormEvent) {
    e.preventDefault();
    const code = joinCodeInput || initialRoomFromUrl();
    if (!code) {
      setError("Open an invite link, or paste a room code in the address bar (?room=…).");
      return;
    }
    const joinRole = initialRoleFromUrl();
    void enterRoom(code, displayName, joinRole, {
      claimGm: joinRole === "gm",
    });
  }

  const players = members.filter((m) => m.role === "player");
  const ownedNames =
    scene?.tokens
      .filter((t) => t.ownerConnectionId === selfId)
      .map((t) => t.name)
      .join(", ") ?? "";

  if (screen === "room") {
    const presenceLabel =
      members.length === 0
        ? "Connecting…"
        : `${members.length} here · ${
            role === "gm"
              ? "GM"
              : ownedNames
                ? `You: ${ownedNames}`
                : "Waiting for token"
          }`;

    return (
      <main className="play-shell" data-testid="play-shell">
        <div className="play-stage">
          {error ? (
            <p className="play-error" data-testid="error-banner" role="alert">
              {error}
            </p>
          ) : null}
          {rejectNotice && rejectNotice !== error ? (
            <p className="play-error" data-testid="reject-banner" role="alert">
              {rejectNotice}
            </p>
          ) : null}

          {role === "gm" ? (
            <details className="gm-drawer" data-testid="gm-scene-panel">
              <summary>GM tools (optional)</summary>
              <p className="muted gm-hint">
                Preferred: Obsidian Path A bridge. Browser fixtures stay for demos.
              </p>
              <div className="actions-row">
                <button
                  type="button"
                  className="btn primary"
                  data-testid="push-atlas-link-test"
                  onClick={() => void onPushAtlasLinkTest()}
                >
                  Push Atlas Link Test
                </button>
                <button
                  type="button"
                  className="btn secondary"
                  data-testid="push-port-smoke-test"
                  onClick={() => void onPushPortSmokeTest()}
                >
                  Push PortSmokeTest
                </button>
                <button
                  type="button"
                  className="btn secondary"
                  data-testid="pick-scene-file"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose .atlasmap…
                </button>
                <button
                  type="button"
                  className="btn secondary"
                  data-testid="load-demo"
                  onClick={() => void onLoadDemoAdventure()}
                >
                  Load demo scene.v1
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json,.atlasmap"
                  hidden
                  data-testid="scene-file-input"
                  onChange={(e) => void onFilePick(e)}
                />
              </div>
              <label className="paste-label">
                Paste .atlasmap or scene.v1 JSON
                <textarea
                  data-testid="scene-paste"
                  rows={4}
                  value={scenePaste}
                  onChange={(e) => setScenePaste(e.target.value)}
                />
              </label>
              <div className="actions-row">
                <button
                  type="button"
                  className="btn secondary"
                  data-testid="load-paste"
                  disabled={!scenePaste.trim()}
                  onClick={onPasteLoad}
                >
                  Push pasted JSON
                </button>
                <button
                  type="button"
                  className="btn secondary"
                  data-testid="match-seats"
                  onClick={matchSeatsByOwnerKey}
                >
                  Match seats by ownerKey
                </button>
              </div>
              {scene ? (
                <ul className="assign-list" data-testid="gm-assign-panel">
                  {scene.tokens.map((token) => (
                    <li key={token.id}>
                      <span className="assign-name">
                        {token.name}
                        {token.ownerKey ? (
                          <span className="assign-key"> · {token.ownerKey}</span>
                        ) : null}
                      </span>
                      <select
                        data-testid={`assign-${token.id}`}
                        aria-label={`Owner for ${token.name}`}
                        value={token.ownerConnectionId ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          assignToken(token.id, v === "" ? null : v);
                        }}
                      >
                        <option value="">Unassigned (GM only)</option>
                        {players.map((p) => (
                          <option key={p.connectionId} value={p.connectionId}>
                            {p.displayName}
                          </option>
                        ))}
                      </select>
                    </li>
                  ))}
                </ul>
              ) : null}
              {loadNote ? (
                <p className="ok-note" data-testid="load-note">
                  {loadNote}
                </p>
              ) : null}
              <p className="muted" data-testid="gm-session">
                GM session: {gmSessionClaimed || gmToken ? "claimed" : "legacy"} ·{" "}
                {assetBackend ?? "…"}
              </p>
            </details>
          ) : null}

          {scene ? (
            <div
              className="play-board-viewport"
              ref={viewportRef}
              onPointerDown={onViewportPointerDown}
              onPointerUp={onViewportPointerUp}
              onPointerCancel={onViewportPointerUp}
              onWheel={onViewportWheel}
              data-testid="board-viewport"
              data-panning={panRef.current ? "true" : "false"}
            >
              <div
                className="play-board board"
                ref={boardRef}
                style={{
                  width: scene.map.width,
                  height: scene.map.height,
                  transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.zoom})`,
                }}
                data-testid="board"
                data-scene-id={scene.id}
                data-zoom={camera.zoom.toFixed(3)}
              >
                <img
                  className="board-map"
                  src={scene.map.url}
                  alt=""
                  width={scene.map.width}
                  height={scene.map.height}
                  draggable={false}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    const w = img.naturalWidth;
                    const h = img.naturalHeight;
                    if (w < 1 || h < 1) return;
                    setScene((prev) => {
                      if (!prev) return prev;
                      if (prev.map.width === w && prev.map.height === h) {
                        return prev;
                      }
                      return {
                        ...prev,
                        map: { ...prev.map, width: w, height: h },
                      };
                    });
                  }}
                />
                {scene.map.grid && (scene.map.grid.size > 0 || scene.map.grid.enabled !== false) ? (() => {
                  const patternId = `board-grid-pattern-${scene.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
                  const gSize = scene.map.grid.size && scene.map.grid.size > 0 ? scene.map.grid.size : TOKEN_SIZE;
                  const gColor = scene.map.grid.color || "rgba(255, 255, 255, 0.4)";
                  const gOpacity = typeof scene.map.grid.opacity === "number" && scene.map.grid.opacity > 0 ? scene.map.grid.opacity : 0.4;
                  return (
                    <svg
                      className="board-grid"
                      data-testid="board-grid"
                      width={scene.map.width}
                      height={scene.map.height}
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 2,
                        opacity: gOpacity,
                      }}
                    >
                      <defs>
                        <pattern
                          id={patternId}
                          width={gSize}
                          height={gSize}
                          x={scene.map.grid.offsetX ?? 0}
                          y={scene.map.grid.offsetY ?? 0}
                          patternUnits="userSpaceOnUse"
                        >
                          <rect
                            width={gSize}
                            height={gSize}
                            fill="none"
                            stroke={gColor}
                            strokeWidth={scene.map.grid.lineWidth || 1}
                          />
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
                    </svg>
                  );
                })() : null}
                {scene.tokens.map((token) => {
                  const movable = canMoveToken(token, selfId, role);
                  const mine = token.ownerConnectionId === selfId;
                  const gmOnly = token.gmOnly === true;
                  const gridSquare =
                    typeof scene.map.grid?.size === "number" && scene.map.grid.size > 0
                      ? scene.map.grid.size
                      : TOKEN_SIZE;
                  const diameter = Math.max(
                    gridSquare,
                    typeof token.size === "number" && token.size > 0
                      ? token.size
                      : gridSquare,
                  );
                  const ringOn = token.showRing === true;
                  const ringColor = token.ringColor || "#C4A574";
                  const showNameplate = token.showNameplate === true;
                  const hp = token.hp ?? null;
                  const stress = token.stress ?? null;
                  const hpPct =
                    hp && hp.max > 0
                      ? Math.max(0, Math.min(100, (hp.current / hp.max) * 100))
                      : null;
                  const stressPct =
                    stress && stress.max > 0
                      ? Math.max(
                          0,
                          Math.min(100, (stress.current / stress.max) * 100),
                        )
                      : null;
                  return (
                    <button
                      key={token.id}
                      type="button"
                      className="token"
                      data-token-id={token.id}
                      data-owned={mine ? "true" : "false"}
                      data-movable={movable ? "true" : "false"}
                      data-gm-only={gmOnly ? "true" : "false"}
                      data-show-ring={ringOn ? "true" : "false"}
                      data-show-nameplate={showNameplate ? "true" : "false"}
                      data-owner-label={ownerLabel(token, members)}
                      data-size={String(diameter)}
                      style={{
                        left: `${(token.x / scene.map.width) * 100}%`,
                        top: `${(token.y / scene.map.height) * 100}%`,
                        width: `${(diameter / scene.map.width) * 100}%`,
                        ...(ringOn
                          ? ({
                              ["--token-ring" as string]: ringColor,
                            } as CSSProperties)
                          : null),
                        ...(hpPct !== null
                          ? ({
                              ["--hp-percent" as string]: String(hpPct),
                            } as CSSProperties)
                          : null),
                        ...(stressPct !== null
                          ? ({
                              ["--stress-percent" as string]: String(stressPct),
                            } as CSSProperties)
                          : null),
                      }}
                      onPointerEnter={(e) => {
                        setHoveredToken(token);
                        if (e.metaKey || e.ctrlKey) {
                          if (canOpenTokenSheet(token, role, selfId, displayName)) {
                            setOpenTokenSheet(token);
                          }
                        }
                      }}
                      onPointerLeave={() =>
                        setHoveredToken((cur) => (cur?.id === token.id ? null : cur))
                      }
                      onPointerDown={(e) => {
                        if (e.button === 0) {
                          onTokenPointerDown(token, e);
                        }
                      }}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (canOpenTokenSheet(token, role, selfId, displayName)) {
                            setOpenTokenSheet(token);
                          }
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTokenContextMenu({
                          token,
                          x: e.clientX,
                          y: e.clientY,
                        });
                      }}
                      aria-label={`${token.name}${movable ? " (drag to move)" : " (not yours)"}`}
                      title={
                        movable
                          ? `${token.name} — drag to move (Cmd+hover for stats/notes)`
                          : `${token.name} — ${ownerLabel(token, members)} (Cmd+hover for stats/notes)`
                      }
                    >
                      <span className="token-chrome" aria-hidden>
                        {(hp || stress) && (
                          <span className="token-status-row">
                            {hp && (
                              <span
                                className="token-bar token-bar--hp"
                                title={`HP ${hp.current}/${hp.max}`}
                              >
                                <span className="token-bar__fill" />
                              </span>
                            )}
                            {stress && (
                              <span
                                className="token-bar token-bar--stress"
                                title={`Stress ${stress.current}/${stress.max}`}
                              >
                                <span className="token-bar__fill" />
                              </span>
                            )}
                          </span>
                        )}
                        <span className="token-avatar">
                          <img src={token.artUrl} alt="" draggable={false} />
                        </span>
                        {showNameplate ? (
                          <span className="token-nameplate">{token.name}</span>
                        ) : (
                          <span className="token-label">{token.name}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
                {(scene.pins ?? []).map((pin) => {
                  const isCreatePin = /create\s*character/i.test(pin.label.trim());
                  return (
                    <button
                      key={pin.id}
                      type="button"
                      className={`map-pin ${isCreatePin ? "map-pin--create-character" : ""}`}
                      data-testid="map-pin"
                      data-pin-id={pin.id}
                      data-pin-icon={normalizePinIcon(pin.icon)}
                      data-gm-only={pin.gmOnly === true ? "true" : "false"}
                      style={{
                        left: `${(pin.x / scene.map.width) * 100}%`,
                        top: `${(pin.y / scene.map.height) * 100}%`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isCreatePin) {
                          setCharacterCreatorOpen(true);
                        } else {
                          setOpenPinId(pin.id);
                        }
                      }}
                      onPointerEnter={(e) => {
                        setHoveredPin(pin);
                        if (e.metaKey || e.ctrlKey) {
                          if (isCreatePin) setCharacterCreatorOpen(true);
                          else setOpenPinId(pin.id);
                        }
                      }}
                      onPointerLeave={() =>
                        setHoveredPin((cur: PinState | null) => (cur?.id === pin.id ? null : cur))
                      }
                      onPointerDown={(e) => e.stopPropagation()}
                      aria-label={`Pin ${pin.label}`}
                      title={isCreatePin ? "Click to open Character Creator" : pin.label}
                    >
                      <PinIconGlyph icon={isCreatePin ? "lore" : pin.icon} />
                      <span className="map-pin-label">{pin.label}</span>
                    </button>
                  );
                })}
                {(scene.widgets ?? []).map((w, idx) => {
                  const color = w.color || "var(--petrol)";
                  // Use explicit position if set, otherwise layout across the board top/corners
                  const posX = typeof w.x === "number" ? w.x : (70 + (idx % 6) * 160);
                  const posY = typeof w.y === "number" ? w.y : (40 + Math.floor(idx / 6) * 80);
                  const isClock = w.type === "clock";
                  const segments = w.segments ?? w.max ?? 6;
                  const filled = Math.max(0, Math.min(segments, Math.round(w.value)));
                  return (
                    <div
                      key={w.id}
                      className={`map-widget map-widget--${w.type}`}
                      data-testid="map-widget"
                      data-widget-id={w.id}
                      data-widget-type={w.type}
                      style={{
                        left: `${(posX / scene.map.width) * 100}%`,
                        top: `${(posY / scene.map.height) * 100}%`,
                        ["--widget-accent" as string]: color,
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      title={`${w.label}: ${w.value}${w.max != null ? `/${w.max}` : ""}`}
                    >
                      <span className="map-widget-label">{w.label}</span>
                      {isClock ? (
                        <div
                          className="map-widget-segments"
                          aria-label={`${w.label} ${filled} of ${segments}`}
                        >
                          {Array.from({ length: segments }, (_, i) => (
                            <span
                              key={i}
                              className={
                                i < filled
                                  ? "board-widget-seg is-filled"
                                  : "board-widget-seg"
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="map-widget-value" data-testid="widget-counter-value">
                          {w.value}
                          {w.max != null ? (
                            <span className="board-widget-max">/{w.max}</span>
                          ) : null}
                        </span>
                      )}
                    </div>
                  );
                })}
                <FogOverlay
                  fog={scene.fog ?? []}
                  mapWidth={scene.map.width}
                  mapHeight={scene.map.height}
                  role={role}
                  tokens={scene.tokens}
                />
              </div>
            </div>
          ) : (
            <p className="play-waiting" data-testid="waiting-scene">
              {role === "gm"
                ? "Push a scene from Obsidian (Path A eye / Push) to put the map on the board."
                : "Waiting for the GM to push a scene…"}
            </p>
          )}

          {/* Initiative Tracker */}
          {scene ? (() => {
            const initiativeTokenIds = scene.initiative && scene.initiative.length > 0
              ? scene.initiative
              : scene.tokens.filter((t) => !t.gmOnly).map((t) => t.id);
            const initiativeTokens = initiativeTokenIds
              .map((id) => scene.tokens.find((t) => t.id === id))
              .filter((t): t is TokenState => Boolean(t && (role === "gm" || !t.gmOnly)));
            if (initiativeTokens.length === 0) return null;
            return (
              <div
                className="board-initiative-tracker"
                data-testid="initiative-tracker"
                aria-label="Initiative Tracker"
              >
                <div className="initiative-title">Initiative</div>
                <div className="initiative-list">
                  {initiativeTokens.map((tok, idx) => {
                    const isActive = idx === 0;
                    const canActivate = true;
                    return (
                      <div
                        key={tok.id}
                        className={`initiative-item ${isActive ? "is-active" : ""}`}
                        data-token-id={tok.id}
                        data-active={isActive ? "true" : "false"}
                      >
                        <div className="initiative-avatar">
                          <img src={tok.artUrl} alt="" draggable={false} />
                          {isActive ? <span className="initiative-badge">Turn</span> : null}
                        </div>
                        <div className="initiative-info">
                          <span className="initiative-name">{tok.name}</span>
                          <span className="initiative-seat">{ownerLabel(tok, members)}</span>
                        </div>
                        {canActivate ? (
                          <button
                            type="button"
                            className="btn-initiative-activate"
                            title={`Activate ${tok.name} and move to bottom of initiative`}
                            onClick={() => {
                              sendJson({ type: "initiative.activate", tokenId: tok.id });
                            }}
                          >
                            Activate
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })() : null}

          {/* Left-edge Slide-out Card Hand Panel */}
          <aside
            className={`hand-panel ${handOpen ? "is-open" : ""}`}
            data-testid="hand-panel"
            aria-label="Hand of Cards"
            aria-hidden={!handOpen}
          >
            <div className="hand-panel-head">
              <h3>My Hand ({handCards.length})</h3>
              <button
                type="button"
                className="btn secondary btn-tiny"
                data-testid="hand-close"
                onClick={() => setHandOpen(false)}
                title="Close hand"
              >
                Close
              </button>
            </div>

            <div className="hand-panel-body">
              <div className="hand-add-section">
                <p className="hand-hint">Add any card image from URL or device:</p>
                <div className="hand-add-inputs">
                  <input
                    type="text"
                    placeholder="Card title (optional)"
                    className="hand-input"
                    value={newCardName}
                    onChange={(e) => setNewCardName(e.target.value)}
                  />
                  <div className="hand-url-row">
                    <input
                      type="url"
                      placeholder="https://example.com/card.png"
                      className="hand-input"
                      value={newCardUrl}
                      onChange={(e) => setNewCardUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addCardByUrl(newCardUrl, newCardName);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn secondary btn-tiny"
                      onClick={() => addCardByUrl(newCardUrl, newCardName)}
                      disabled={!newCardUrl.trim()}
                    >
                      Add URL
                    </button>
                  </div>
                  <div className="hand-file-row">
                    <button
                      type="button"
                      className="btn secondary btn-tiny"
                      onClick={() => cardFileInputRef.current?.click()}
                    >
                      Upload image…
                    </button>
                    <input
                      ref={cardFileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleCardFileSelect}
                    />
                  </div>
                </div>
              </div>

              <div className="hand-cards-list">
                {handCards.length === 0 ? (
                  <p className="muted hand-empty">No cards in hand. Add one above.</p>
                ) : (
                  handCards.map((card) => (
                    <div key={card.id} className="hand-card-item">
                      <div
                        className="hand-card-thumb-wrap"
                        onClick={() => setPreviewCard(card)}
                        title="Click to view full card"
                      >
                        <img src={card.imageUrl} alt={card.name || "Card"} className="hand-card-thumb" />
                      </div>
                      <div className="hand-card-meta">
                        <span className="hand-card-title">{card.name || "Card"}</span>
                        <div className="hand-card-actions">
                          <button
                            type="button"
                            className="btn secondary btn-tiny"
                            onClick={() => setPreviewCard(card)}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="btn secondary btn-tiny hand-card-del"
                            onClick={() => removeCard(card.id)}
                            title="Remove card from hand"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </aside>

          {/* Card Full-view Preview Modal */}
          {previewCard && (
            <div
              className="hand-preview-modal-backdrop"
              onClick={() => setPreviewCard(null)}
            >
              <div
                className="hand-preview-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="hand-preview-head">
                  <h4>{previewCard.name || "Card View"}</h4>
                  <button
                    type="button"
                    className="btn secondary btn-tiny"
                    onClick={() => setPreviewCard(null)}
                  >
                    Close
                  </button>
                </div>
                <div className="hand-preview-img-wrap">
                  <img
                    src={previewCard.imageUrl}
                    alt={previewCard.name || "Card"}
                    className="hand-preview-img"
                  />
                </div>
              </div>
            </div>
          )}

          {openTokenSheet && canOpenTokenSheet(openTokenSheet, role, selfId, displayName) ? (
            <PcStatblockSheet
              token={
                scene?.tokens.find((t) => t.id === openTokenSheet.id) ??
                openTokenSheet
              }
              noteText={
                (scene?.tokens.find((t) => t.id === openTokenSheet.id)
                  ?.noteContent ||
                  (tokenSheetNote?.tokenId === openTokenSheet.id
                    ? tokenSheetNote.text
                    : "")) ??
                ""
              }
              isOwner={playerOwnsToken(openTokenSheet, selfId, displayName)}
              role={role}
              ownerLabel={ownerLabel(openTokenSheet, members)}
              onClose={() => setOpenTokenSheet(null)}
              onRoll={publishBoardRoll}
              onPrepareRoll={() => setRollResult(null)}
              onUpdateHp={(clamped) => {
                const maxHp = openTokenSheet.hp?.max ?? 6;
                setScene((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    tokens: prev.tokens.map((t) =>
                      t.id === openTokenSheet.id
                        ? { ...t, hp: { current: clamped, max: maxHp } }
                        : t,
                    ),
                  };
                });
                setOpenTokenSheet((prev) =>
                  prev ? { ...prev, hp: { current: clamped, max: maxHp } } : null,
                );
              }}
              onUpdateStress={(clamped) => {
                const maxStress = openTokenSheet.stress?.max ?? 6;
                setScene((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    tokens: prev.tokens.map((t) =>
                      t.id === openTokenSheet.id
                        ? { ...t, stress: { current: clamped, max: maxStress } }
                        : t,
                    ),
                  };
                });
                setOpenTokenSheet((prev) =>
                  prev
                    ? { ...prev, stress: { current: clamped, max: maxStress } }
                    : null,
                );
              }}
              onUpdateArt={
                playerOwnsToken(openTokenSheet, selfId, displayName) || role === "gm"
                  ? (file) => {
                      void (async () => {
                        try {
                          const res = await fetch(
                            `/parties/main/${encodeURIComponent(roomCode)}/assets`,
                            {
                              method: "POST",
                              headers: {
                                "Content-Type": file.type || "image/png",
                                "X-Filename": file.name.slice(0, 128),
                              },
                              body: await file.arrayBuffer(),
                            },
                          );
                          const body = (await res.json().catch(() => null)) as {
                            url?: string;
                            error?: string;
                          } | null;
                          if (!res.ok || typeof body?.url !== "string") {
                            throw new Error(body?.error || `Upload failed (${res.status})`);
                          }
                          sendJson({
                            type: "token.update",
                            tokenId: openTokenSheet.id,
                            artUrl: body.url,
                          });
                          setScene((prev) => {
                            if (!prev) return prev;
                            return {
                              ...prev,
                              tokens: prev.tokens.map((t) =>
                                t.id === openTokenSheet.id
                                  ? { ...t, artUrl: body.url! }
                                  : t,
                              ),
                            };
                          });
                          setOpenTokenSheet((prev) =>
                            prev ? { ...prev, artUrl: body.url! } : null,
                          );
                        } catch (err) {
                          setError(
                            err instanceof Error ? err.message : "Could not update art",
                          );
                        }
                      })();
                    }
                  : undefined
              }
            />
          ) : null}

          {rollResult ? (
            <PathADiceTray roll={rollResult} onClose={() => setRollResult(null)} />
          ) : null}

          {/* Right-click Token Permissions Menu (Stays open after click) */}
          {tokenContextMenu && (
            <div
              className="board-context-menu"
              style={{
                position: "fixed",
                left: Math.min(window.innerWidth - 220, tokenContextMenu.x),
                top: Math.min(window.innerHeight - 260, tokenContextMenu.y),
                zIndex: 10000,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="board-context-header">
                <span>Permissions: {tokenContextMenu.token.name}</span>
                <button
                  type="button"
                  className="btn secondary btn-tiny"
                  onClick={() => setTokenContextMenu(null)}
                >
                  ×
                </button>
              </div>
              <div className="board-context-body">
                <button
                  type="button"
                  className={`board-context-item ${tokenContextMenu.token.ownerConnectionId === null && !tokenContextMenu.token.gmOnly ? "is-selected" : ""}`}
                  onClick={() => {
                    assignToken(tokenContextMenu.token.id, null);
                    setTokenContextMenu((prev) =>
                      prev
                        ? {
                            ...prev,
                            token: { ...prev.token, ownerConnectionId: null, gmOnly: false },
                          }
                        : null,
                    );
                  }}
                >
                  {tokenContextMenu.token.ownerConnectionId === null && !tokenContextMenu.token.gmOnly ? "✓ " : ""}Public (All players)
                </button>

                <button
                  type="button"
                  className={`board-context-item ${tokenContextMenu.token.gmOnly ? "is-selected" : ""}`}
                  onClick={() => {
                    assignToken(tokenContextMenu.token.id, null);
                    setTokenContextMenu((prev) =>
                      prev
                        ? {
                            ...prev,
                            token: { ...prev.token, ownerConnectionId: null, gmOnly: true },
                          }
                        : null,
                    );
                  }}
                >
                  {tokenContextMenu.token.gmOnly ? "✓ " : ""}GM (Just me)
                </button>

                <div className="board-context-divider">Specific player:</div>
                {players.length === 0 ? (
                  <div className="board-context-item is-disabled">(No players seated)</div>
                ) : (
                  players.map((p) => {
                    const isAssigned = tokenContextMenu.token.ownerConnectionId === p.connectionId;
                    return (
                      <button
                        key={p.connectionId}
                        type="button"
                        className={`board-context-item ${isAssigned ? "is-selected" : ""}`}
                        onClick={() => {
                          assignToken(tokenContextMenu.token.id, p.connectionId);
                          // Stays open after click!
                          setTokenContextMenu((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  token: {
                                    ...prev.token,
                                    ownerConnectionId: p.connectionId,
                                    gmOnly: false,
                                  },
                                }
                              : null,
                          );
                        }}
                      >
                        {isAssigned ? "✓ " : ""}{p.displayName}
                      </button>
                    );
                  })
                )}
                {role === "gm" ? (
                  <>
                    <div className="board-context-divider">Token</div>
                    <button
                      type="button"
                      className="board-context-item"
                      data-testid="token-delete"
                      onClick={() => {
                        sendJson({
                          type: "token.delete",
                          tokenId: tokenContextMenu.token.id,
                        });
                        setTokenContextMenu(null);
                      }}
                    >
                      Delete token
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          )}

          {/* CC-4 Character Creator Wizard Modal */}
          <CharacterCreatorWizard
            isOpen={characterCreatorOpen}
            onClose={() => setCharacterCreatorOpen(false)}
            playerName={displayName.trim() || "Player"}
            onConfirmCharacter={(char) => {
              handleCharacterConfirmed(char);
            }}
          />

          {scene && openPinId
            ? (() => {
                const pin = (scene.pins ?? []).find((p) => p.id === openPinId);
                if (!pin) return null;
                return (
                  <aside
                    className="sheet-panel"
                    data-testid="sheet-panel"
                    aria-label={`Sheet ${pin.label}`}
                  >
                    <div className="sheet-panel-head">
                      <h3>{pin.label}</h3>
                      <button
                        type="button"
                        className="btn secondary btn-tiny"
                        data-testid="sheet-close"
                        onClick={() => setOpenPinId(null)}
                      >
                        Close
                      </button>
                    </div>
                    {pin.notePath ? (
                      <p className="muted sheet-path">{pin.notePath}</p>
                    ) : null}
                    <pre className="sheet-body" data-testid="sheet-body">
                      {sheetBodyText(pin, sheetFetch)}
                    </pre>
                  </aside>
                );
              })()
            : null}
        </div>

        <footer className="play-toolbar" aria-label="Table toolbar">
          <div className="play-toolbar-meta">
            <p className="play-toolbar-title">
              {scene?.name ?? "Path A"}
            </p>
            <p className="play-toolbar-sub" data-testid="room-code">
              Room {roomCode}
            </p>
            <span className="presence-chip" data-testid="owned-tokens">
              {presenceLabel}
              {connState !== "open" ? ` · ${connState}` : ""}
              {lastMoveMs !== null ? ` · ${lastMoveMs}ms` : ""}
            </span>
            {copyNote ? (
              <span className="presence-chip" data-testid="copy-note">
                {copyNote}
              </span>
            ) : null}
          </div>
          <div className="play-toolbar-actions" aria-label="Camera controls">
            {scene ? (
              <>
                <button
                  type="button"
                  className="btn secondary btn-tiny"
                  data-testid="zoom-out"
                  onClick={() => nudgeZoom(-1)}
                >
                  −
                </button>
                <span className="camera-zoom" data-testid="camera-zoom">
                  {Math.round(camera.zoom * 100)}%
                </span>
                <button
                  type="button"
                  className="btn secondary btn-tiny"
                  data-testid="zoom-in"
                  onClick={() => nudgeZoom(1)}
                >
                  +
                </button>
                <button
                  type="button"
                  className="btn secondary btn-tiny"
                  data-testid="reset-camera"
                  onClick={resetCamera}
                >
                  Reset
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="btn secondary btn-tiny create-char-pin-btn"
              data-testid="open-create-character-pin"
              onClick={() => setCharacterCreatorOpen(true)}
              title="Create character (Daggerheart SRD 2.0 Level 1 Wizard)"
            >
              Create character
            </button>
            <button
              type="button"
              className={`btn secondary btn-tiny hand-toggle-btn ${handOpen ? "is-active" : ""}`}
              data-testid="toggle-hand-panel"
              onClick={() => setHandOpen((prev) => !prev)}
              title="Open or close your hand of cards"
            >
              Cards ({handCards.length})
            </button>
            <button
              type="button"
              className="btn secondary btn-tiny"
              data-testid="copy-link"
              onClick={() =>
                void copyText("invite link", roomShareUrl(roomCode))
              }
            >
              Copy URL
            </button>
            <button
              type="button"
              className="btn primary btn-tiny"
              onClick={disconnect}
            >
              Leave
            </button>
          </div>
        </footer>
      </main>
    );
  }

  return (
    <main className="shell">
      <p className="brand">Techniclee</p>
      <h1>Join room</h1>
      {joinCodeInput ? (
        <p className="join-hint" data-testid="invite-banner">
          Room <code className="code-lg">{joinCodeInput}</code>
        </p>
      ) : (
        <p className="join-hint">
          Open an invite link from your GM, then enter your display name.
        </p>
      )}

      <form className="panel form" onSubmit={onLobbySubmit}>
        <label>
          Display name
          <input
            type="text"
            name="displayName"
            autoComplete="nickname"
            maxLength={32}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Alex"
            required
          />
        </label>
        <div className="actions-row">
          <button
            type="submit"
            className="btn primary"
            disabled={!joinCodeInput || !displayName.trim()}
            data-testid="join-room"
          >
            Join room
          </button>
        </div>
        {error ? <p className="err">{error}</p> : null}
      </form>
    </main>
  );
}

const root = document.getElementById("app");
if (!root) {
  throw new Error("#app missing");
}
createRoot(root).render(<App />);
